import { css, html, LitElement } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html';

const MAX_ICON = '&#10138;';
const MIN_ICON = '&#10136;';
const CLOSE_ICON = '&#10006;';

class GWorksWindow extends LitElement {
  static get properties () {
    return {
      closable: { reflect: true, type: Boolean },
      label: { reflect: true, type: String },
      show: { reflect: true, type: Boolean }
    };
  }

  static get styles () {
    return css`:host,
      *,
      *::after,
      *::before {
        box-sizing: border-box;
      }

      :host {
        display: block;
        max-height: calc(100vh - 100px);
      }

      .title-bar {
        align-items: flex-start;
        background-color: var(--titlebar-bg-color, #396430);
        color: var(--titlebar-text-color, white);
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        font-family: Roboto, sans-serif;
        line-height: 2.5;
        width: 100%;
      }

      .title-bar button {
        background-color: transparent;
        border: 0;
        color: inherit;
        cursor: pointer;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
      }

      .title-bar button:focus {
        outline: 0.125rem dotted currentColor;
      }

      .title-bar__title-btn {
        padding: 0;
        text-align: left;
        width: 100%;
      }

      .title-bar__left {
        flex: 1 1 auto;
        padding-left: 0.5rem;
      }

      .title-bar__right {
        display: flex;
        flex-wrap: nowrap;
      }

      .container {
        background-color: var(--content-bg-color, rgba(242, 248, 241, 0.8));
        height: 100%;
        max-height: calc(var(--content-max-height, 70vh) - 2.5em);
        overflow: auto;
      }

      @media screen and (prefers-reduced-motion: no-preference) {
        .container {
          animation: slide-down 150ms ease-in;
          will-change: transform, opacity;
        }
      }

      @keyframes slide-down {
        from {
          opacity: 0;
          transform: scaleY(0.5), translateY(-50%);
        }

        to {
          opacity: 1;
          transform: scaleY(1), translateY(0%);
        }
      }
`;
  }

  constructor () {
    super();

    this.closable = false;
    this.label = '';
    /** @type {boolean} */
    this.show = false;
  }

  connectedCallback () {
    super.connectedCallback();

    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', this.label);
  }

  /**
   * @param {Map<string, any>} changedProperties
   */
  update (changedProperties) {
    if (changedProperties.has('label')) {
      this.setAttribute('aria-label', this.label);
    }

    super.update(changedProperties);
  }

  disconnectedCallback () {
    super.disconnectedCallback();
  }

  /**
   * @private
   */
  onToggleClick () {
    this.toggle();
  }

  /**
   * Emits `toggle` event
   */
  toggle () {
    const newShow = !this.show;

    this.show = newShow;

    this.dispatchEvent(
      new CustomEvent('toggle', { bubbles: true, detail: { show: newShow } })
    );

    return newShow;
  }

  /**
   * @private
   */
  onCloseClick () {
    this.close();
  }

  /**
   * Emits `close` event and removes self from DOM
   */
  close () {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
    this.remove();
  }

  render () {
    return html`<div class="title-bar">
        <div class="title-bar__left">
          <button
            class="title-bar__title-btn"
            type="button"
            aria-expanded="${this.show ? 'true' : 'false'}"
            aria-controls="content"
            @click=${this.onToggleClick}
          >
            ${this.label}
          </button>
        </div>
        <div class="title-bar__right">
          <button
            class="title-bar__toggle-btn"
            type="button"
            aria-label="Show Content"
            aria-expanded="${this.show ? 'true' : 'false'}"
            aria-controls="content"
            @click="${this.onToggleClick}"
          >
            <span aria-hidden="true"
              >${this.show ? unsafeHTML(MAX_ICON) : unsafeHTML(MIN_ICON)}</span
            ></button
          ><button
            class="title-bar__close-btn"
            type="button"
            aria-label="Close"
            @click="${this.onCloseClick}"
            ?hidden=${!this.closable}
          >
            <span aria-hidden="true">${unsafeHTML(CLOSE_ICON)}</span>
          </button>
        </div>
      </div>
      <div id="content" class="container" ?hidden=${!this.show}>
        <slot></slot>
      </div>`;
  }
}

customElements.define('gworks-window', GWorksWindow);

export default GWorksWindow;
